// Benachrichtigungen der Glocke aus dem Kern (coreApi, live über useCoreEventInvalidation in der Shell).
import type { NotificationItem } from '@bar/ui/basis';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { coreApi, coreKeys } from '@web/lib';
import type { PlatformKey } from '@web/registry/types';
import { useMemo } from 'react';

export const PLATFORM_PREFIX: Record<PlatformKey, '' | '/admin' | '/ai'> = {
  people: '',
  admin: '/admin',
  ai: '/ai',
};

/** Server-URLs sind absolut (mit Plattform-Präfix); innerhalb der eigenen Plattform relativ routen. */
export function localHref(url: string, platform: PlatformKey): { href: string; reload: boolean } {
  const prefix = PLATFORM_PREFIX[platform];
  const isPrefixed = (p: string) => url === p || url.startsWith(`${p}/`);
  const foreign = (Object.keys(PLATFORM_PREFIX) as PlatformKey[])
    .filter((p) => p !== platform && PLATFORM_PREFIX[p])
    .some((p) => isPrefixed(PLATFORM_PREFIX[p]));
  if (prefix && isPrefixed(prefix)) return { href: url.slice(prefix.length) || '/', reload: false };
  if (foreign) return { href: url, reload: true };
  // Unpräfixierte URLs gehören der People-App.
  return { href: url, reload: prefix !== '' };
}

const BELL_KEY = [...coreKeys.notificationsAll, 'bell'] as const;

/** Die letzten 30 Benachrichtigungen und ihre Aktionen. */
export function useShellNotifications(platform: PlatformKey) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: BELL_KEY,
    queryFn: () => coreApi.notifications({ limit: 30 }),
    retry: false,
  });
  const items = useMemo<NotificationItem[]>(
    () =>
      (query.data?.items ?? []).map((n) => {
        const target = n.url ? localHref(n.url, platform) : undefined;
        return {
          id: n.id,
          title: n.title,
          body: n.body ?? undefined,
          createdAt: n.createdAt,
          read: n.readAt !== null,
          href: target?.href,
          reload: target?.reload,
          mention: /mention|dm|direct/i.test(n.kind),
        };
      }),
    [query.data, platform],
  );
  const invalidate = () => client.invalidateQueries({ queryKey: coreKeys.notificationsAll });
  const markRead = useMutation({
    mutationFn: coreApi.markNotificationRead,
    onSettled: invalidate,
  });
  const markAll = useMutation({
    mutationFn: coreApi.markAllNotificationsRead,
    onSettled: invalidate,
  });
  return {
    items,
    loading: query.isPending,
    error: query.isError,
    retry: () => void query.refetch(),
    markRead: (id: string) => markRead.mutate(id),
    markAllRead: () => markAll.mutate(),
  };
}
